import React, { ReactNode } from 'react';
import {
  Avatar, Box, Button, Container, Flex, HStack, Link as ChakraLink, Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useInventoryAuth } from '../../lib/inventory/InventoryAuthContext';

/**
 * Dedicated shell for the inventory sub-domain site. Deliberately does NOT use
 * the course-booking chrome (header nav / basket / ribbon background) — this is
 * a focused self-service tool, so it gets its own branded top bar and a calm
 * neutral canvas.
 */
const InventoryLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { borrower, signOut } = useInventoryAuth();

  return (
    <Box minH="100vh" w="100%" bg="gray.50">
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={10}
        bg="blue.900"
        color="white"
        borderBottom="3px solid"
        borderColor="yellow.900"
      >
        <Container maxW="6xl" px={{ base: 4, md: 6 }}>
          <Flex h="64px" align="center" justify="space-between">
            <ChakraLink as={NextLink} href="/inventory" _hover={{ textDecoration: 'none' }}>
              <HStack spacing={3} align="center">
                <Box
                  boxSize="34px"
                  borderRadius="md"
                  bg="yellow.900"
                  color="blue.900"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="lg"
                >
                  ✦
                </Box>
                <Box lineHeight="1.1">
                  <Text fontSize="xs" letterSpacing="widest" opacity={0.7} textTransform="uppercase">
                    UKCPA
                  </Text>
                  <Text fontWeight="bold" fontSize="md">Costume &amp; Prop Store</Text>
                </Box>
              </HStack>
            </ChakraLink>

            {borrower && (
              <HStack spacing={3}>
                <HStack
                  spacing={2}
                  bg="whiteAlpha.200"
                  borderRadius="full"
                  pl={1}
                  pr={3}
                  py={1}
                >
                  <Avatar
                    size="xs"
                    name={`${borrower.firstName} ${borrower.lastName}`}
                    bg="pink.900"
                    color="white"
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    {borrower.firstName}
                    {' '}
                    {borrower.lastName}
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  onClick={signOut}
                >
                  Sign out
                </Button>
              </HStack>
            )}
          </Flex>
        </Container>
      </Box>

      <Container as="main" maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 6, md: 10 }}>
        {children}
      </Container>
    </Box>
  );
};

export default InventoryLayout;
